import { useState, useRef } from "react";
import { Upload, X, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { mockClients } from "../mockData";
import type { MockClient } from "../types";

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (files: File[], clientId: string, mirrorClientId: string | null) => void;
  preselectedClientId?: string;
}

export function UploadDialog({
  open,
  onOpenChange,
  onUpload,
  preselectedClientId,
}: UploadDialogProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [clientId, setClientId] = useState(preselectedClientId || "");
  const [mirrorClientId, setMirrorClientId] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (files.length === 0 || !clientId) return;
    onUpload(files, clientId, mirrorClientId || null);
    setFiles([]);
    setClientId(preselectedClientId || "");
    setMirrorClientId("");
    onOpenChange(false);
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setFiles([]);
      setMirrorClientId("");
    }
    onOpenChange(isOpen);
  };

  const availableMirrorClients = mockClients.filter((c: MockClient) => c.id !== clientId);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-neutral-300 rounded-lg p-8 text-center cursor-pointer hover:border-neutral-400 transition-colors"
          >
            <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
            <p className="text-sm text-neutral-600">
              Drop files here or click to browse
            </p>
            <p className="text-xs text-neutral-400 mt-1">
              PDF, Images, Excel, Word — max 20MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.tiff,.bmp,.doc,.docx,.xls,.xlsx,.csv"
            />
          </div>

          {/* Selected files */}
          {files.length > 0 && (
            <div className="space-y-1.5">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-2 p-2 bg-neutral-50 rounded text-sm"
                >
                  <FileText className="w-4 h-4 text-neutral-500 shrink-0" />
                  <span className="truncate flex-1">{file.name}</span>
                  <span className="text-xs text-neutral-400 shrink-0">
                    {(file.size / 1024).toFixed(0)} KB
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Client selector */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Client</label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {mockClients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mirror client selector */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Mirror to client{" "}
              <span className="text-neutral-400 font-normal">(optional)</span>
            </label>
            <Select value={mirrorClientId} onValueChange={setMirrorClientId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="No mirroring" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No mirroring</SelectItem>
                {availableMirrorClients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={files.length === 0 || !clientId}>
            Upload {files.length > 0 && `(${files.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
